import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, MessageCircle, Send, CheckCircle, Phone } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'general',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setIsSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-gold uppercase tracking-[0.3em] text-sm mb-4">Get in Touch</p>
            <h1 className="font-cinzel text-4xl sm:text-5xl lg:text-6xl text-foreground mb-6">
              Contact <span className="text-gold">Us</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Have questions? Need prayer? Want to get involved? We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-cinzel text-2xl text-gold mb-8">Reach Out</h2>
              
              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex-shrink-0 rounded-full bg-gold/10 flex items-center justify-center">
                    <Mail className="text-gold" size={24} />
                  </div>
                  <div>
                    <h3 className="text-foreground font-semibold mb-1">Email</h3>
                    <a href="mailto:4thefam@truebelieverfam.com" className="text-muted-foreground hover:text-gold transition-colors">
                      4thefam@truebelieverfam.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex-shrink-0 rounded-full bg-gold/10 flex items-center justify-center">
                    <Phone className="text-gold" size={24} />
                  </div>
                  <div>
                    <h3 className="text-foreground font-semibold mb-1">Phone</h3>
                    <a href="tel:281-914-0109" className="text-muted-foreground hover:text-gold transition-colors">
                      (281) 914-0109
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex-shrink-0 rounded-full bg-gold/10 flex items-center justify-center">
                    <MapPin className="text-gold" size={24} />
                  </div>
                  <div>
                    <h3 className="text-foreground font-semibold mb-1">Location</h3>
                    <p className="text-muted-foreground">10303 Northwest Freeway</p>
                    <p className="text-muted-foreground">Suite 263</p>
                    <p className="text-muted-foreground">Houston, TX 77092</p>
                    <a 
                      href="https://www.google.com/maps/search/?api=1&query=10303+Northwest+Freeway+Suite+263+Houston+TX+77092"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gold text-sm mt-2 inline-block hover:underline"
                    >
                      Get Directions →
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex-shrink-0 rounded-full bg-gold/10 flex items-center justify-center">
                    <MessageCircle className="text-gold" size={24} />
                  </div>
                  <div>
                    <h3 className="text-foreground font-semibold mb-1">Social Media</h3>
                    <div className="flex gap-4 mt-2">
                      <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-gold transition-colors">
                        Facebook
                      </a>
                      <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-gold transition-colors">
                        YouTube
                      </a>
                      <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-gold transition-colors">
                        Instagram
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Prayer Request Info */}
              <div className="card-dark">
                <h3 className="font-cinzel text-lg text-foreground mb-3">Need Prayer?</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Select "Prayer Request" as the subject in the contact form, and our team will lift you up in prayer. All requests are kept confidential.
                </p>
                <p className="text-gold text-sm italic">
                  "Therefore confess your sins to each other and pray for each other so that you may be healed." — James 5:16
                </p>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {isSubmitted ? (
                <div className="card-dark text-center py-16" data-testid="contact-success">
                  <div className="w-20 h-20 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="text-gold" size={48} />
                  </div>
                  <h3 className="font-cinzel text-2xl text-foreground mb-4">Message Sent!</h3>
                  <p className="text-muted-foreground mb-6">
                    Thank you for reaching out. We'll get back to you as soon as possible.
                  </p>
                  <button
                    onClick={() => {
                      setIsSubmitted(false);
                      setFormData({ name: '', email: '', subject: 'general', message: '' });
                    }}
                    className="text-gold text-sm uppercase tracking-wider"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <label htmlFor="name" className="block text-sm text-foreground mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full bg-muted border border-border rounded-sm px-4 py-3 text-foreground focus:border-gold focus:outline-none transition-colors"
                      data-testid="input-name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm text-foreground mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-muted border border-border rounded-sm px-4 py-3 text-foreground focus:border-gold focus:outline-none transition-colors"
                      data-testid="input-email"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm text-foreground mb-2">
                      Subject *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full bg-muted border border-border rounded-sm px-4 py-3 text-foreground focus:border-gold focus:outline-none transition-colors"
                      data-testid="select-subject"
                    >
                      <option value="general">General Inquiry</option>
                      <option value="prayer">Prayer Request</option>
                      <option value="volunteer">Volunteer Interest</option>
                      <option value="membership">Membership Question</option>
                      <option value="outreach">Outreach & Donations</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm text-foreground mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full bg-muted border border-border rounded-sm px-4 py-3 text-foreground focus:border-gold focus:outline-none transition-colors resize-none"
                      data-testid="textarea-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="submit-contact"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Message
                        <Send size={18} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
